import React, { useMemo, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import { useFetchProjectsQuery } from "../../store/api/projectsApi";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { getTaskDelayDays, getTaskHealth } from "./adminData";
import "./admin.css";

const ProjectProgressAnalyticsPage = () => {
  const { data: projects = [], isLoading, error } = useFetchProjectsQuery();
  const [projectFilter, setProjectFilter] = useState("all");
  const [delayedPage, setDelayedPage] = useState(1);
  const DELAYED_PAGE_SIZE = 10;

  const filteredProjects = useMemo(
    () => (projectFilter === "all" ? projects : projects.filter((project) => project.id === projectFilter)),
    [projectFilter, projects]
  );

  const chartRows = filteredProjects.flatMap((project) =>
    project.progressTasks.map((task) => ({
      id: task.id,
      label: `${project.title} - ${task.moduleName}`,
      project: project.title,
      moduleName: task.moduleName,
      plannedPercent: task.plannedPercent,
      actualPercent: task.actualPercent,
      plannedEndDate: task.plannedEndDate,
      health: getTaskHealth(task),
      delayDays: getTaskDelayDays(task),
    }))
  );

  const delayedTasks = chartRows.filter((row) => row.health === "delayed");
  const delayedTotalPages = Math.max(1, Math.ceil(delayedTasks.length / DELAYED_PAGE_SIZE));
  const pagedDelayed = delayedTasks.slice((delayedPage - 1) * DELAYED_PAGE_SIZE, delayedPage * DELAYED_PAGE_SIZE);
  const completedTasks = chartRows.filter((row) => row.health === "completed");
  const onTrackTasks = chartRows.filter((row) => row.health === "on-track");
  const errorMessage = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  const progressChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    colors: ["#1d4ed8", "#16a34a"],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 6,
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: false },
    xaxis: {
      categories: chartRows.map((row) => row.moduleName),
      labels: {
        rotate: -30,
      },
    },
    yaxis: {
      max: 100,
      title: {
        text: "Progress %",
      },
    },
    legend: {
      position: "top",
    },
    tooltip: {
      y: {
        formatter: (value) => `${value}%`,
      },
    },
  };

  const progressSeries = [
    {
      name: "Planned",
      data: chartRows.map((row) => row.plannedPercent),
    },
    {
      name: "Actual",
      data: chartRows.map((row) => row.actualPercent),
    },
  ];

  const healthChartOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: { show: false },
    },
    labels: ["On Track", "Delayed", "Completed"],
    colors: ["#2563eb", "#dc2626", "#16a34a"],
    legend: {
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
    },
  };

  const healthSeries = [onTrackTasks.length, delayedTasks.length, completedTasks.length];

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        {errorMessage ? <div className='alert alert-danger mb-4'>{errorMessage}</div> : null}

        {/* Main analytics card */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a", marginBottom: "1.5rem" }}>
          {/* Gradient header */}
          <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className='bi bi-graph-up-arrow' style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Project Progress Analytics</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Compare planned vs actual and surface delayed modules</div>
              </div>
            </div>
          </div>
          {/* Filter bar */}
          <div className='d-flex flex-wrap gap-3 align-items-center p-3 border-bottom'>
            <div style={{ minWidth: 240 }}>
              <select
                id='projectProgressFilter'
                className='form-select form-select-sm'
                value={projectFilter}
                onChange={(event) => setProjectFilter(event.target.value)}
                disabled={isLoading}
                title='Filter by project'
                aria-label='Filter by project'
              >
                <option value='all'>All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </div>
            <span className='small text-muted ms-auto'>{chartRows.length} module{chartRows.length !== 1 ? "s" : ""}</span>
          </div>
          <div className='card-body p-4'>
            <div className='admin-stats-grid'>
              <article className='admin-stat-card'>
                <p>Total Modules</p>
                <h3>{isLoading ? "..." : chartRows.length}</h3>
                <span>Task packages currently tracked in the selected scope</span>
              </article>
              <article className='admin-stat-card'>
                <p>Delayed Modules</p>
                <h3>{isLoading ? "..." : delayedTasks.length}</h3>
                <span>Modules behind plan based on progress or end-date slip</span>
              </article>
              <article className='admin-stat-card'>
                <p>On-Track Modules</p>
                <h3>{isLoading ? "..." : onTrackTasks.length}</h3>
                <span>Modules progressing within the expected schedule</span>
              </article>
              <article className='admin-stat-card'>
                <p>Completed Modules</p>
                <h3>{isLoading ? "..." : completedTasks.length}</h3>
                <span>Modules that have reached 100% completion</span>
              </article>
            </div>
          </div>
        </div>

        {isLoading ? <div className='alert alert-light border'>Loading progress analytics...</div> : null}

        {!isLoading && chartRows.length > 0 ? (
          <div className='row g-4'>
            <div className='col-xl-8'>
              <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a", height: "100%" }}>
                <div style={{ background: "linear-gradient(135deg, #1e3a5f, #1d4ed8, #3b82f6)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className='bi bi-bar-chart-fill' style={{ color: "#fff", fontSize: 13 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#fff", lineHeight: 1.2 }}>Planned vs Actual Progress</div>
                    <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)" }}>Large gap between bars indicates modules needing attention</div>
                  </div>
                </div>
                <div className='card-body p-4'>
                  <ReactApexChart options={progressChartOptions} series={progressSeries} type='bar' height={360} />
                </div>
              </div>
            </div>

            <div className='col-xl-4'>
              <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a", height: "100%" }}>
                <div style={{ background: "linear-gradient(135deg, #3b0764, #7c3aed, #a855f7)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className='bi bi-pie-chart-fill' style={{ color: "#fff", fontSize: 13 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#fff", lineHeight: 1.2 }}>Health Mix</div>
                    <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)" }}>Distribution of modules by current delivery health</div>
                  </div>
                </div>
                <div className='card-body p-4'>
                  <ReactApexChart options={healthChartOptions} series={healthSeries} type='donut' height={360} />
                </div>
              </div>
            </div>

            <div className='col-12'>
              <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
                <div style={{ background: "linear-gradient(135deg, #78350f, #d97706, #f59e0b)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className='bi bi-exclamation-triangle-fill' style={{ color: "#fff", fontSize: 13 }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#fff", lineHeight: 1.2 }}>Delayed Module Watchlist</div>
                      <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)" }}>Modules flagged with slip amount for admin escalation</div>
                    </div>
                  </div>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>
                    {delayedTasks.length} delayed
                  </span>
                </div>
                <div className='card-body p-4'>
                  {delayedTasks.length > 0 ? (
                    <>
                    <div className='table-responsive'>
                      <table className='table align-middle mb-0'>
                        <thead className='table-light'>
                          <tr>
                            <th>#</th>
                            <th>Project</th>
                            <th>Module</th>
                            <th>Planned</th>
                            <th>Actual</th>
                            <th>Planned End</th>
                            <th>Delay</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedDelayed.map((task, idx) => (
                            <tr key={task.id}>
                              <td className='text-muted'>{(delayedPage - 1) * DELAYED_PAGE_SIZE + idx + 1}</td>
                              <td>{task.project}</td>
                              <td>{task.moduleName}</td>
                              <td>{task.plannedPercent}%</td>
                              <td>{task.actualPercent}%</td>
                              <td>{task.plannedEndDate}</td>
                              <td>{task.delayDays} day(s)</td>
                              <td>
                                <span className='badge text-bg-danger rounded-pill px-3 py-2'>Delayed</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <TablePagination
                      currentPage={delayedPage}
                      totalPages={delayedTotalPages}
                      totalItems={delayedTasks.length}
                      pageSize={DELAYED_PAGE_SIZE}
                      onPageChange={setDelayedPage}
                    />
                    </>
                  ) : (
                    <div className='alert alert-success mb-0'>No delayed modules in the selected scope.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!isLoading && chartRows.length === 0 && !errorMessage ? (
          <div className='alert alert-light border mb-0'>No progress data is available yet. Add task progress from the progress tracker page.</div>
        ) : null}
      </div>
    </section>
  );
};

export default ProjectProgressAnalyticsPage;
